import { IBayLevelData, ISizeSummary, IVesselParts } from "open-vessel-definition";
import { ONE_MILLIMETER_IN_FEET, SVG_WIDTH, TCG_IN_MMM } from "./consts";

import { BayLcgVcgTcgAndPairings } from "./BayLcgVcgTcgAndPairings/BayLcgVcgTcgAndPairings";
import { IVesselOnePartWithCalcs } from "./types/IVesselOnePart";
import { calculatePartsForVesselOne } from "./calculatePartsForVesselOne";
import { preCalculateCurrentVesselParts } from "./preCalculateCurrentVesselParts";
import { roundDec } from "@tedivo/tedivo-pure-helpers";

/**
 * Generate coordinate functions for the vessel model: X=LCG, Y=VCG(BottomBases), Z=TCG
 * @param param0
 * @returns
 */
export function generateCoordFunctions({
  baysData,
  vesselPartsData,
  bayLcvVcgTcgAndPairings,
  svgElementMeasures,
  maxHeightAbsolute: maxHeight,
}: IGenerateCoordFunctionsProps): IGeneratedCoordFunctionsResult | undefined {
  const {
    lcgNormalization = SVG_WIDTH,
    areaContsFwd,
    areaContsAft,
    deckWidth,
    paddingShip,
    paddingContArea,
  } = svgElementMeasures;

  const { minLcg, maxLcg, minBottomBase, maxTcg, minTcg } = bayLcvVcgTcgAndPairings;

  if (maxHeight === undefined || maxTcg === undefined) {
    return undefined; // --> FAST_RETURN: No data to calculate
  }

  const preCalculatedSlots = preCalculateCurrentVesselParts(baysData, vesselPartsData || []);

  const vOneBaySlots = calculatePartsForVesselOne(
    preCalculatedSlots,
    bayLcvVcgTcgAndPairings,
    baysData,
  );

  if (vOneBaySlots.length === 0) {
    return undefined; // --> FAST_RETURN: No slots to calculate
  }

  const areaForContainers = areaContsFwd - areaContsAft;
  const allPartsLengthInMeters = maxLcg - minLcg;
  const svgSizeRatio = areaForContainers / allPartsLengthInMeters;

  const scaled = (n: number) => n * svgSizeRatio;
  const unscaled = (n: number) => n / svgSizeRatio;

  const sternLength = unscaled(areaContsAft);
  const bowLength = unscaled(lcgNormalization - areaContsFwd);

  const modelDeckBaseWidthInMM = roundDec(unscaled(deckWidth), 2);
  const requiredWidth = Math.abs(minTcg - maxTcg) + TCG_IN_MMM;

  const xScale = 1;
  const yScale = 1;
  const zScale = (requiredWidth + unscaled(paddingContArea.z * 2)) / modelDeckBaseWidthInMM;

  const minBottomBaseSafe = minBottomBase === Infinity ? 0 : minBottomBase;

  const addX = roundDec(areaContsAft + paddingShip.x + paddingContArea.x),
    addY = roundDec(paddingShip.y + paddingContArea.y),
    addZ = roundDec(paddingShip.z + paddingContArea.z);

  const zRange = roundDec(scaled(requiredWidth) + paddingContArea.z * 2 + paddingShip.z * 2);

  const xRange = roundDec(scaled(allPartsLengthInMeters + sternLength + bowLength));

  const yRange = roundDec(
    scaled(maxHeight - minBottomBaseSafe) + svgElementMeasures.paddingShip.y * 3,
  );

  return {
    addX,
    addY,
    addZ,
    xScale,
    yScale,
    zScale,
    xRange,
    yRange,
    zRange,
    xPos: (lcg: number, d = 2) => roundDec(scaled(lcg - minLcg) + addX, d),
    yPos: (vcg: number, d = 2) => roundDec(scaled(-vcg + maxHeight) + addY, d),
    zPos: (tcg: number, d = 2) => roundDec(scaled(tcg - minTcg + TCG_IN_MMM * 0.5) + addZ, d),
    scaled,
    unscaled,
    svgSizeRatio,
    sternLength,
    bowLength,
    allPartsLengthInMeters,
    lowestLcgAft: minLcg,
    vOneBaySlots,
    oneInchScaled: scaled(1 / ONE_MILLIMETER_IN_FEET) * svgSizeRatio,
  };
}

interface IGenerateCoordFunctionsProps {
  sizeSummary: ISizeSummary;
  baysData: IBayLevelData[];
  vesselPartsData: IVesselParts[];
  bayLcvVcgTcgAndPairings: BayLcgVcgTcgAndPairings;
  maxHeightAbsolute: number;
  svgElementMeasures: IReturnTypeGetMeasuresFromAttrs;
}

export interface IReturnTypeGetMeasuresFromAttrs {
  lcgNormalization: number;
  totalHeight: number;
  areaContsAft: number;
  areaContsFwd: number;
  deckWidth: number;
  deckPadWidth: number;
  sideHeight: number;
  bowTopHeight: number;
  translatedPartsX: number;
  translatedPartsY: number;
  paddingShip: {
    x: number;
    y: number;
    z: number;
  };
  paddingContArea: {
    x: number;
    y: number;
    z: number;
  };
  parts: {
    bridge: { length: number; width: number };
    smokestack: { length: number; width: number };
    crane: { length: number; width: number };
  };
}

interface IGeneratedCoordFunctionsResult {
  /** Add units in LCG (x) */
  addX: number;
  /** Add units in VCG BottomBases (y) */
  addY: number;
  /** Add units in TCG (z) */
  addZ: number;
  xRange: number;
  yRange: number;
  zRange: number;
  xScale: number;
  yScale: number;
  zScale: number;
  xPos: (lcg: number, d?: number) => number;
  yPos: (vcg: number, d?: number) => number;
  zPos: (tcg: number, d?: number) => number;
  scaled: (n: number) => number;
  unscaled: (n: number) => number;
  svgSizeRatio: number;
  sternLength: number;
  bowLength: number;
  allPartsLengthInMeters: number;
  lowestLcgAft: number;
  vOneBaySlots: Array<IVesselOnePartWithCalcs>;
  oneInchScaled: number;
}
